import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useHistory } from 'react-router-dom';
import { Layout, Menu, Avatar, LayoutProps, Image } from 'antd';
import { MenuUnfoldOutlined, MenuFoldOutlined } from '@ant-design/icons';
import { RouterProps } from 'router/routeUrl';
import styles from './index.module.less';
import classnames from 'classnames';
import defaultLogo from 'assets/imgs/logo_white.png';
import defaultLogoBlack from 'assets/imgs/logo_black.png';
import deputyLoginImg from 'assets/imgs/adv/deputy-login.png';
import iconMenu from 'assets/imgs/menu.png';
import iconMenuBlack from 'assets/imgs/menu_black.png';
import iconMenuLose from 'assets/imgs/menu_close.png';
import iconOpen from 'assets/imgs/open.png';

import MenuLayer from 'components/MenuLayer';
import { useScroll } from 'ahooks';
import { useTranslation } from 'react-i18next';
import { getAllLanguages, getLanguageConfig } from 'utils/language';
import { switchLanguageInPath, getLanguageFromPath, removeLanguagePrefix } from 'utils/languageRoute';
import { useLanguagePath } from 'hooks/useLanguageRoute';
import languages from '../../locales/languages';

const sideLayout = false; // layout布局，默认 false 为顶部横向菜单，设置 true 为侧边栏菜单
const { Header, Content, Sider } = Layout;

interface BaseLayoutProps extends LayoutProps {
  className?: string;
  menu?: RouterProps[];
  homePath?: string;
  logoSrc?: string;
  logoDarkSrc?: string;
}

const BaseLayout: FC<BaseLayoutProps> = (props) => {
  let location = useLocation();
  const history = useHistory();
  const {
    className,
    children,
    menu = [],
    homePath = '/home',
    logoSrc = defaultLogo,
    logoDarkSrc = defaultLogoBlack,
  } = props;
  const [selectedKeys, setSelectedKeys] = useState<string>();
  const [collapsed, changeCollapsed] = useState<boolean>(false);
  const [showMenu, setShowMenu] = useState<boolean>(false);
  const [advertisementScreen, setAdvertisementScreen] = useState<number>(0);
  const [showLanguageOptions, setShowLanguageOptions] = useState<boolean>(false);
  const { t, i18n } = useTranslation();
  const languageDropdownRef = useRef<HTMLDivElement>(null);

  const scroll = useScroll(document);
  const supportedLanguages = getAllLanguages();
  const currentLanguage = (i18n.language || 'en').split('-')[0];
  const currentLanguageConfig = getLanguageConfig(currentLanguage);
  const currentLanguageLabel = currentLanguageConfig?.label || 'EN';
  const getLanguagePath = useLanguagePath();

  const isWhiteTheme = useMemo(() => {
    return location.pathname.includes('/info/');
  }, [location]);

  const isAdvertisementPage = useMemo(() => {
    return removeLanguagePrefix(location.pathname) === '/advertisement';
  }, [location]);

  const isDownloadLandingPage = useMemo(() => {
    return removeLanguagePrefix(location.pathname) === '/blockblast/gp/apphome';
  }, [location]);

  useEffect(() => {
    setSelectedKeys(location.pathname);
  }, [location]);

  // 根据语言设置字体模式
  useEffect(() => {
    const currentLang = getLanguageFromPath(location.pathname) || 'en';
    const langConfig = languages.find((l) => l.code === currentLang);

    // 设置语言属性，用于 CSS 选择器
    document.documentElement.setAttribute('data-lang', currentLang);

    // 设置是否使用自定义字体（默认为 true）
    if (langConfig?.useCustomFont !== false) {
      document.documentElement.setAttribute('data-custom-font', 'true');
    } else {
      document.documentElement.removeAttribute('data-custom-font');
    }
  }, [location.pathname]);

  useEffect(() => {
    if (showMenu) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [showMenu]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        showLanguageOptions &&
        languageDropdownRef.current &&
        !languageDropdownRef.current.contains(event.target as Node)
      ) {
        setShowLanguageOptions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showLanguageOptions]);

  // 监听 advertisement 页面的屏幕切换
  useEffect(() => {
    const handleScreenChange = (e: CustomEvent) => {
      setAdvertisementScreen(e.detail.screen);
    };

    window.addEventListener('advertisementScreenChange', handleScreenChange as EventListener);

    return () => {
      window.removeEventListener('advertisementScreenChange', handleScreenChange as EventListener);
    };
  }, []);

  const handleLanguageSelect = (langCode: string) => {
    // 切换语言并跳转到对应语言的路径（无刷新）
    const newPath = switchLanguageInPath(location.pathname, langCode);
    i18n.changeLanguage(langCode);
    history.push(newPath);
    setShowLanguageOptions(false);
  };

  return sideLayout ? (
    // 菜单侧边布局
    <Layout className={classnames(styles.sideLayout, className)}>
      <Sider trigger={null} collapsible collapsed={collapsed}>
        <Link className={styles.logo} to={getLanguagePath(homePath)}>
          <Image preview={false} src="" />
        </Link>
        <Menu className={styles.headerMenu} mode="inline" selectedKeys={[]}>
          {menu
            ?.filter((item) => {
              if (!item.menuName) return false;
              // 根据语言配置动态隐藏导航
              const langConfig = getLanguageConfig(currentLanguage);
              if (langConfig && item.path && langConfig.hiddenPaths.includes(item.path)) {
                return false;
              }
              return true;
            })
            ?.map((item) => (
              <Menu.Item key={item.path} icon={item.menuIcon}>
                <Link to={item.path}>{t(item.menuName)}</Link>
              </Menu.Item>
            ))}
        </Menu>
      </Sider>
      <Layout className={styles.siteLayout}>
        <Header className={styles.header} style={{ padding: 0 }}>
          {collapsed ? (
            <MenuUnfoldOutlined className={styles.icon} onClick={() => changeCollapsed(false)} />
          ) : (
            <MenuFoldOutlined className={styles.icon} onClick={() => changeCollapsed(true)} />
          )}
          <Avatar src="https://guanwang-1301026175.cos.ap-beijing.myqcloud.com/ee/2021090617011328459f8598a-ee3b-4d3c-baf5-bee72dc2d85d.png" />
        </Header>
        <Content>{children}</Content>
      </Layout>
    </Layout>
  ) : (
    // 菜单顶部布局
    <div
      className={classnames(styles.baseLayoutContainer, {
        [styles['info-page']]: isWhiteTheme,
        [styles['advertisement-page']]: isAdvertisementPage,
        [styles['download-landing-page']]: isDownloadLandingPage,
      })}
    >
      <Layout className={classnames(styles.baseLayout, className)}>
        <Header
          className={classnames(styles.header, {
            [styles.isScroll]: scroll && scroll.top > 20 && !showMenu && isWhiteTheme,
          })}
        >
          <div className={styles.headerContent}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <Link className={styles.logo} to={getLanguagePath(homePath)} onClick={() => setShowMenu(false)}>
                <Image
                  preview={false}
                  src={isWhiteTheme && !showMenu ? logoDarkSrc : logoSrc}
                  height="60px"
                  style={{ width: 'auto' }}
                />
              </Link>
              {isAdvertisementPage && (
                <Image preview={false} src={deputyLoginImg} height="60px" style={{ width: 'auto' }} />
              )}
            </div>

            <div className={styles.headerActions}>
              <div className={styles.languageDropdown} ref={languageDropdownRef}>
                <button
                  type="button"
                  className={styles.languageToggle}
                  onClick={() => setShowLanguageOptions((prev) => !prev)}
                >
                  <span>{currentLanguageLabel}</span>
                  <Image
                    preview={false}
                    src={iconOpen}
                    width=".16rem"
                    className={classnames(styles.languageIcon, { [styles.languageIconOpen]: showLanguageOptions })}
                  />
                </button>
                {showLanguageOptions && (
                  <div className={styles.languageOptions}>
                    {supportedLanguages.map((lang) => (
                      <button
                        key={lang.code}
                        type="button"
                        className={classnames(styles.languageOption, {
                          [styles.languageOptionActive]: currentLanguage === lang.code,
                        })}
                        onClick={() => handleLanguageSelect(lang.code)}
                      >
                        {lang.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className={classnames('flex cursor-pointer', styles.menu)} onClick={() => setShowMenu(!showMenu)}>
                {showMenu ? null : <span>{t('nav.menu')}</span>}

                {!showMenu && <Image preview={false} src={isWhiteTheme ? iconMenuBlack : iconMenu} width=".75rem" />}

                {showMenu && (
                  <div className="" style={{ width: '.75rem' }}>
                    <Image preview={false} src={iconMenuLose} height=".34rem" />
                  </div>
                )}
              </div>
            </div>
          </div>
        </Header>

        <MenuLayer
          menuItems={menu}
          showMenuLayer={showMenu}
          onMenuItemClick={() =>
            setTimeout(() => {
              setShowMenu(false);
            }, 200)
          }
        />

        <Content>{children}</Content>
      </Layout>
    </div>
  );
};

export default BaseLayout;
